import React from "react";
import "./style.scss";
import AddAPhotoIcon from "@material-ui/icons/AddAPhoto";
import { useInputImageId } from "./useInputImageId";
import { css, cx } from "emotion";
import CloseIcon from "@material-ui/icons/Close";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
// import AppService from "../../Services/AppService/AppService";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteIcon from "@material-ui/icons/Delete";
import { IconButton } from "@material-ui/core";
import AppService from "../../Services/AppService/AppService";

function readFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

function resizeImage(base64Str, maxWidth = 300, maxHeight = 300) {
  return new Promise((resolve) => {
    let img = new Image();
    img.src = base64Str;
    img.onload = () => {
      let canvas = document.createElement("canvas");
      const MAX_WIDTH = maxWidth;
      const MAX_HEIGHT = maxHeight;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }
      canvas.width = width;
      canvas.height = height;
      let ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      let png64 = canvas.toDataURL("image/png", "1");
      resolve(png64);
    };
  });
}

const NewImageUploader = React.forwardRef((props, ref) => {
  const {
    image,
    footerNote,
    errorNote,
    maxSize,
    aspectRatio,
    width,
    height,
    onUploadDone,
    onRemove,
    background,
    size,
    ratioParams,
    compID,
    heightAuto,
    useCropper,
  } = props;
  const refInputImage = React.useRef();
  const componentID = useInputImageId();
  const cropperRef = React.useRef(null);

  const [openImageCropper, setOpenImageCropper] = React.useState(false);
  const [imageSrc, setImageSrc] = React.useState("");
  const [croppedImage, setCroppedImage] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isError, setIsError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [availableImage, setAvailableImage] = React.useState(image);
  const [uploaderSize, setUploaderSize] = React.useState(size);
  const [dimension, setDimension] = React.useState(100);
  const heightPercent = ((height / width) * 100)

  const uploadImage = (image) => {
    return new Promise((resolve) => {
      AppService.uploaderFile({ file: image })
        .then((response) => {
          setLoading(false);
          setIsError(false);
          setAvailableImage(response.data.url);
          onUploadDone(response.data.url);
          resolve(true);
        })
        .catch((error) => {
          let response = error.response;

          setLoading(false);
          setIsError(true);

          if (response.status === 500) {
            setErrorMessage(
              "Failed upload image, please compress your image to a smaller size"
            );
          } else {
            setErrorMessage(response.data.message);
          }

          resolve(false);
        });
    });
  };

  const handleToggleImageCropper = () => {
    setOpenImageCropper(!openImageCropper);
    refInputImage.current.value = null;
  };

  const handleOnChangeFile = async (event) => {
    setErrorMessage("");
    setIsError(false);

    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file.size <= maxSize * 1024) {
        if (file.name.match(/.(gif)$/i) || !useCropper) {
          let imageDataUrl = await readFile(file);
          setImageSrc(imageDataUrl);
          setErrorMessage("");
          setIsError(false);
          setLoading(true);
          uploadImage(imageDataUrl);
        } else {
          let imageDataUrl = await readFile(file);
          setImageSrc(imageDataUrl);
          setErrorMessage("");
          setIsError(false);
          setOpenImageCropper(true);
        }
      } else {
        setErrorMessage("Max file size " + maxSize + "kb ");
        setIsError(true);
      }
    }
  };

  const onCrop = () => {
    const imageElement = cropperRef?.current;
    const cropper = imageElement?.cropper;
    const cropRes = cropper.getCroppedCanvas().toDataURL();
    let widthSize = width || 300;
    let heightSize = height || 300;

    if (!width && !height) {
      switch (aspectRatio) {
        case 16 / 9 || 16 / 9:
          widthSize = 640;
          heightSize = 360;
          break;

        default:
          widthSize = 300;
          heightSize = 300;
          break;
      }
    }

    resizeImage(cropRes, widthSize, heightSize).then((result) => {
      setCroppedImage(result);
    });
  };

  const handleReset = () => {
    const imageElement = cropperRef?.current;
    const cropper = imageElement?.cropper;
    cropper.reset();
  };

  const handleClickRemove = () => {
    setAvailableImage("");
    setLoading(false);
    setIsError(false);
    setErrorMessage("");
    refInputImage.current.value = null;
    onRemove(availableImage);
  };

  const handleCropDone = () => {
    setAvailableImage("");
    setLoading(true);
    uploadImage(croppedImage).then(() => {
      handleToggleImageCropper();
    });
  };

  React.useEffect(() => {
    setAvailableImage(image);
  }, [image]);

  React.useEffect(() => {
    switch (uploaderSize) {
      case "sm":
        setDimension(100);
        break;
      case "md":
        setDimension(200);
        break;
      case "lg":
        setDimension(300);
        break;
      case "auto":
        setDimension(-1);
        break;
      default:
        setDimension(100);
        break;
    }
  }, [uploaderSize]);

  return (
    <div
      className="d-inline-block"
      ref={ref}
      style={
        dimension > 0
          ? { maxWidth: (dimension || 100) + "px" }
          : { width: "100%" }
      }
      key={compID}
    >
      <input
        type="file"
        key={componentID}
        className="input-file-image"
        id={`file-image-${componentID}`}
        accept="image/x-png,image/gif,image/jpeg"
        onChange={(event) => handleOnChangeFile(event)}
        ref={refInputImage}
      />
      <div
        className={cx(
          css`
            border-radius: 8px !important;
            background: ${background || "#eeeeee"} !important ;
            width: ${(dimension > 0 ? dimension + "px" : "100%") ||
            "100px"} !important;
            height: ${(dimension > 0 ? dimension + "px" : "auto") ||
            "100px"} !important;
            padding-top: ${dimension < 0 && !heightAuto && height > 0
              ? heightPercent + "%"
              : "auto"} !important;
            position: relative !important;
          `,
          "input-image-box"
        )}
      >
        {loading && (
          <label
            className={cx(
              css`
                position: ${heightAuto ? "unset !important" : "absolute"};
                ${heightAuto ? " padding: 20px; " : ""}
              `,
              "input-image-inner"
            )}
            htmlFor={`file-image-${componentID}`}
          >
            <i className="fa fa-spin fa-spinner"></i>
            <span className="d-block font-9 mt-2">Uploading ...</span>
          </label>
        )}
        {!loading && availableImage !== "" && (
          <IconButton
            aria-label="remove"
            className="btn-remove"
            size="small"
            onClick={() => handleClickRemove()}
          >
            <DeleteIcon />
          </IconButton>
        )}

        {availableImage === "" && !loading && (
          <label
            className={cx(
              css`
                position: ${heightAuto ? "unset !important" : "absolute"};
                ${heightAuto ? " padding: 20px; " : ""}
              `,
              "input-image-inner"
            )}
            htmlFor={`file-image-${componentID}`}
          >
            <AddAPhotoIcon style={{ width: 32, height: 32 }} />
            <span className="font-10 font-bold text-primary">Upload</span>
          </label>
        )}
        {availableImage !== "" && (
          <div
            className={cx(
              css`
                position: ${heightAuto ? "unset !important" : "absolute"};
                ${heightAuto ? " padding: 20px; " : ""}
              `,
              "input-image-inner"
            )}
          >
            <img
              src={availableImage}
              alt="result"
              style={
                ratioParams === "height"
                  ? { height: "90%", borderRadius: 8, maxWidth: "90%" }
                  : { width: "90%", borderRadius: 8, maxHeight: "90%" }
              }
            />
          </div>
        )}
      </div>
      {footerNote && footerNote !== "" && (
        <label className="font-10 text-gray-757575 w-100 text-center">
          {footerNote}
        </label>
      )}
      {errorNote && errorNote !== "" && (
        <label className="font-10 text-error w-100 text-center">
          {errorNote}
        </label>
      )}
      {isError && (
        <label className="font-10 text-error w-100 text-center">
          {errorMessage}
        </label>
      )}
      <div
        className={cx(
          css`
            background: rgba(0, 0, 0, 0.3);
          `,
          openImageCropper
            ? "modal d-block fade-in-fwd"
            : "modal d-none fade-out-bck"
        )}
        role="dialog"
      >
        <div
          className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
          role="dialog"
        >
          <div
            className={cx(
              css`
                background: none !important;
                border: none !important;
                z-index: 1500 !important;
              `,
              "modal-content modal-cropper-imageuploader"
            )}
          >
            <div className="modal-header bg-white p-4 border-bottom-1-secondary">
              <div className="d-flex w-100 justify-content-between align-items-center">
                <h2 className="font-bold font-20">Edit Image</h2>
                <span
                  onClick={handleToggleImageCropper}
                  className="cursor-pointer"
                >
                  <CloseIcon />
                </span>
              </div>
            </div>
            <div className="modal-body bg-white">
              {!loading && !isError && (
                <div className="card-body p-3">
                  <Cropper
                    src={imageSrc || ""}
                    style={{ width: "auto", height: "calc(80% - 175px)" }}
                    aspectRatio={aspectRatio ? aspectRatio : 1 / 1}
                    guides={false}
                    crop={onCrop}
                    onReset={onCrop}
                    ref={cropperRef}
                  />
                </div>
              )}
              {!loading && isError && (
                <div className="card-body p-3 text-center text-danger">
                  {errorMessage || "Error"}
                </div>
              )}
              {loading && (
                <div className="card-body p-3 text-center">
                  <CircularProgress />
                  <span className="d-block text-center text-gray-757575">
                    Upload image ...
                  </span>
                </div>
              )}
            </div>
            <div className="modal-footer d-block bg-white p-3">
              {!loading && !isError && (
                <div className="d-flex justify-content-center align-items-center">
                  <button
                    type="button"
                    className="btn btn-md text-gray-757575 bg-white border-0 font-12 font-bold px-5 mr-2"
                    onClick={handleReset}
                  >
                    RESET
                  </button>
                  <button
                    type="button"
                    className={cx(
                      css`
                        background-color: #bbdefb !important;
                        color: #2196f3 !important;
                      `,
                      "btn btn-md border-0 font-10 font-bold px-5"
                    )}
                    onClick={handleCropDone}
                  >
                    DONE
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default NewImageUploader;
